<template>
  <v-container fluid class="pa-4">
    <v-breadcrumbs :items="breadcrumbs" class="px-0" />

    <div v-if="loading" class="pa-8 text-center"><v-progress-circular indeterminate /></div>
    <div v-else-if="!question" class="pa-8 text-center"><v-alert type="error">Pregunta no encontrada</v-alert></div>
    <div v-else>
      <v-card class="mb-4 pa-4">
        <h2 class="text-h6 font-weight-bold mb-2">{{ question.code }} — {{ question.text }}</h2>
        <div class="d-flex flex-wrap" style="gap:8px">
          <v-chip x-small>Tipo: {{ fieldTypeLabel(question.fieldType) }}</v-chip>
          <v-chip x-small>Peso: {{ question.weight }}</v-chip>
          <v-chip x-small>Modo: {{ weightModeLabel(question.weightMode) }}</v-chip>
          <v-chip v-if="question.isProfile" x-small color="grey" text-color="white">Perfil</v-chip>
          <v-chip v-if="question.isRequired" x-small color="error" text-color="white">Requerida</v-chip>
        </div>
      </v-card>

      <v-card>
        <v-card-title class="d-flex">
          <span>Opciones</span>
          <v-spacer />
          <v-btn color="primary" small @click="openCreateOption">
            <v-icon left small>mdi-plus</v-icon> Nueva opción
          </v-btn>
        </v-card-title>
        <v-data-table :headers="headers" :items="question.options || []">
            <template #[`item.label`]="{ item }">
                <v-chip small :color="item.color" text-color="white">{{ item.label }}</v-chip>
            </template>
            <template #[`item.color`]="{ item }">
                {{ colorLabel(item.color) }}
            </template>
            <template #[`item.isDefault`]="{ item }">
                <v-icon v-if="item.isDefault" color="success">mdi-check</v-icon>
            </template>
            <template #[`item.actions`]="{ item }">
                <v-btn icon small @click="removeOption(item)">
                    <v-icon color="error">mdi-delete</v-icon>
                </v-btn>
            </template>
        </v-data-table>
      </v-card>
    </div>

    <v-dialog v-model="dialog" max-width="500">
      <v-card>
        <v-card-title>Crear opción</v-card-title>
        <v-card-text>
          <v-text-field v-model="form.label" label="Etiqueta" outlined dense />
          <v-text-field v-model.number="form.value" type="number" label="Valor (puntaje)" outlined dense class="mt-2" />
          <v-select
            v-model="form.color"
            :items="colorOptions"
            item-text="text"
            item-value="value"
            label="Color"
            outlined dense
            class="mt-2"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialog = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveOption">Crear</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-alert type="error" v-if="error" class="mt-3" dismissible @input="error = null">{{ error }}</v-alert>
  </v-container>
</template>

<script>
import { mapMutations } from 'vuex';
import { listRubros, createOption, deleteOption } from '../../api/sac';

export default {
    name: 'SacConfigQuestion',
    data() {
        return {
            loading: true,
            question: null,
            dialog: false,
            saving: false,
            error: null,
            form: { label: '', value: 1, color: 'gray' },
            colorOptions: [
                { value: 'green', text: 'Verde (positivo)' },
                { value: 'yellow', text: 'Amarillo (precaución)' },
                { value: 'red', text: 'Rojo (negativo)' },
                { value: 'gray', text: 'Gris (neutro)' },
            ],
            fieldTypeOptions: [
                { value: 'YES_NO', text: 'Sí / No' },
                { value: 'MULTIPLE', text: 'Opciones múltiples' },
                { value: 'SCALE', text: 'Escala 0-100' },
                { value: 'NUMBER', text: 'Numérico' },
                { value: 'TEXT', text: 'Texto libre' },
            ],
            headers: [
                { text: 'Etiqueta', value: 'label' },
                { text: 'Valor', value: 'value' },
                { text: 'Color', value: 'color' },
                { text: 'Predeterminada', value: 'isDefault' },
                { text: '', value: 'actions', sortable: false, width: 60 },
            ],
        };
    },
    computed: {
        breadcrumbs() {
            const sec = this.question?.section;
            const r = sec?.rubro;
            return [
                { text: 'SAC Config', to: '/sac/admin/config', exact: true },
                ...(r ? [{ text: r.name, to: `/sac/admin/config/rubro/${r._id}` }] : []),
                ...(sec ? [{ text: sec.name, to: `/sac/admin/config/section/${sec._id}` }] : []),
                { text: this.question?.code || '...' },
            ];
        },
    },
    mounted() {
        this.load();
    },
    methods: {
        ...mapMutations('notification', ['show']),
        fieldTypeLabel(v) {
            const o = this.fieldTypeOptions.find((x) => x.value === v);
            return o ? o.text : v;
        },
        weightModeLabel(v) {
            return v === 'ABSOLUTE' ? 'Absoluto' : 'Relativo';
        },
        colorLabel(v) {
            const o = this.colorOptions.find((x) => x.value === v);
            return o ? o.text : v;
        },
        async load() {
            this.loading = true;
            try {
                const res = await listRubros();
                for (const r of res.rubros || []) {
                    for (const sec of r.sections || []) {
                        const q = (sec.questions || []).find((x) => x._id === this.$route.params.id);
                        if (q) {
                            q.section = sec;
                            sec.rubro = r;
                            this.question = q;
                            return;
                        }
                    }
                }
            } catch (e) {
                this.error = e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo cargar la pregunta';
                this.show({ text: this.error, color: 'error' });
            } finally {
                this.loading = false;
            }
        },
        openCreateOption() {
            this.form = { label: '', value: 1, color: 'gray' };
            this.dialog = true;
        },
        async saveOption() {
            this.saving = true;
            try {
                await createOption(this.question._id, this.form);
                this.dialog = false;
                await this.load();
                this.show({ text: 'Opción creada correctamente', color: 'success' });
            } catch (e) {
                this.error = e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo crear la opción';
                this.show({ text: this.error, color: 'error' });
            } finally {
                this.saving = false;
            }
        },
        async removeOption(opt) {
            if (!confirm(`¿Eliminar opción "${opt.label}"?`)) return;
            try {
                await deleteOption(opt._id);
                await this.load();
                this.show({ text: 'Opción eliminada correctamente', color: 'success' });
            } catch (e) {
                this.error = e?.response?.data?.error?.message || e?.response?.data?.message || e.message || 'No se pudo eliminar la opción';
                this.show({ text: this.error, color: 'error' });
            }
        },
    },
};
</script>
